"use client";

import { useTranslations } from "next-intl";
import {
    SectionShell,
    EditorialTitle,
    GradientText,
    AnimatedBar,
    RevealOnScroll,
} from "@/components/ui/Socials/JoinInstructors/_primitives";

// 11% Believemy + 4% transaction = 15% total commission, mentor keeps 85%
const COMMISSION = 0.15;

function netFor(price) {
    return Math.round(price * (1 - COMMISSION));
}

export default function MechanicsSection() {
    const t = useTranslations();

    const cases = [
        {
            id: 1,
            tag: t("joinMentor_mech_case1_tag"),
            duration: t("joinMentor_mech_case1_duration"),
            price: 60,
            sentence: t("joinMentor_mech_case1_sentence"),
        },
        {
            id: 2,
            tag: t("joinMentor_mech_case2_tag"),
            duration: t("joinMentor_mech_case2_duration"),
            price: 120,
            sentence: t("joinMentor_mech_case2_sentence"),
        },
        {
            id: 3,
            tag: t("joinMentor_mech_case3_tag"),
            duration: t("joinMentor_mech_case3_duration"),
            price: 200,
            sentence: t("joinMentor_mech_case3_sentence"),
        },
    ];

    return (
        <SectionShell label={t("joinMentor_mech_marker")}>
            <div className="grid lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 items-start mb-10 sm:mb-14">
                <RevealOnScroll className="lg:col-span-2">
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinMentor_mech_title_part1")}
                        <br />
                        {t("joinMentor_mech_title_part2")}{" "}
                        <GradientText>
                            {t("joinMentor_mech_title_emphasis")}
                        </GradientText>
                        .
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-5 font-mono text-xs space-y-2">
                        <Row
                            k={t("joinMentor_mech_meta_price")}
                            v={t("joinMentor_mech_meta_price_value")}
                        />
                        <Row
                            k={t("joinMentor_mech_meta_payout")}
                            v={t("joinMentor_mech_meta_payout_value")}
                        />
                        <Row
                            k={t("joinMentor_mech_meta_trigger")}
                            v={t("joinMentor_mech_meta_trigger_value")}
                        />
                    </div>
                </RevealOnScroll>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                {cases.map((c, i) => {
                    const net = netFor(c.price);
                    const youPct = Math.round((net / c.price) * 100);
                    return (
                        <RevealOnScroll key={c.id} delay={i * 0.1}>
                            <div className="h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-7 flex flex-col">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-4 font-mono">
                                    {t("joinMentor_mech_case_label")}{" "}
                                    {String(c.id).padStart(2, "0")} · {c.tag}
                                </div>
                                <h3
                                    className="text-xl md:text-2xl text-gray-900 dark:text-white font-bold mb-4 leading-tight"
                                    style={{
                                        fontFamily:
                                            "'Playfair Display', Georgia, serif",
                                    }}
                                >
                                    {t("joinMentor_mech_case_title", {
                                        duration: c.duration,
                                        price: c.price,
                                    })}
                                </h3>

                                <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0E161D] p-3 mb-6 flex items-center justify-between gap-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 font-mono">
                                        {t("joinMentor_mech_billed")}
                                    </div>
                                    <div
                                        className="text-2xl font-bold text-gray-900 dark:text-white"
                                        style={{
                                            fontFamily:
                                                "'Playfair Display', Georgia, serif",
                                        }}
                                    >
                                        {c.price} €
                                    </div>
                                </div>

                                <div className="space-y-3 mb-6">
                                    <div>
                                        <div className="flex justify-between text-[11px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55 mb-1.5 font-mono">
                                            <span>
                                                {t("joinMentor_mech_label_you")}
                                            </span>
                                            <span>{youPct}%</span>
                                        </div>
                                        <AnimatedBar
                                            percent={youPct}
                                            from="from-[#FF7A1A]"
                                            to="to-[#E51578]"
                                            delay={0.4}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-[11px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55 mb-1.5 font-mono">
                                            <span>
                                                {t("joinMentor_mech_label_fees")}
                                            </span>
                                            <span>{100 - youPct}%</span>
                                        </div>
                                        <AnimatedBar
                                            percent={100 - youPct}
                                            delay={0.55}
                                        />
                                    </div>
                                </div>

                                <div className="mt-auto pt-5 border-t border-dashed border-gray-200 dark:border-white/10">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-1 font-mono">
                                        {t("joinMentor_mech_you_get")}
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span
                                            className="text-4xl font-bold text-primary"
                                            style={{
                                                fontFamily:
                                                    "'Playfair Display', Georgia, serif",
                                            }}
                                        >
                                            {net} €
                                        </span>
                                        <span className="text-xs text-gray-400 dark:text-white/40 font-mono">
                                            {c.sentence}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </RevealOnScroll>
                    );
                })}
            </div>

            <RevealOnScroll delay={0.4}>
                <p
                    className="mt-10 text-center italic text-gray-600 dark:text-white/70 max-w-3xl mx-auto"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                    {t("joinMentor_mech_footer_part1")}{" "}
                    <span className="text-primary font-semibold not-italic">
                        {t("joinMentor_mech_footer_emphasis")}
                    </span>
                    {t("joinMentor_mech_footer_part2")}
                </p>
            </RevealOnScroll>
        </SectionShell>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between text-gray-600 dark:text-white/70">
            <span>{k}</span>
            <span className="text-primary">{v}</span>
        </div>
    );
}
